/**
 * Раскрывающаяся группа бокового меню ("Обучение агентов").
 *
 * Развёрнутый сайдбар: кнопка-заголовок раскрывает подпункты с отступом;
 * группа раскрыта сама, когда открыта одна из её страниц; выбор
 * пользователя хранится в localStorage (try/catch: хранилище может быть недоступно).
 * Свёрнутый сайдбар: иконка группы ведёт на первый подпункт.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { OrganizationMode } from '@/types/settings';

export interface SidebarLink {
  icon: LucideIcon;
  label: string;
  path: string;
  ownerOnly?: boolean;
  /** KAN-743: пункт только для перечисленных режимов кабинета; без поля для всех. */
  modes?: OrganizationMode[];
  /** KAN-988: пункт только при функции тарифа (код lib/features.ts); без поля для всех. */
  feature?: string;
  /** KAN-980: пункт только суперпользователю платформы (is_superuser из /auth/me). */
  superuserOnly?: boolean;
}

interface SidebarGroupProps {
  id: string;
  icon: LucideIcon;
  label: string;
  items: SidebarLink[];
  isActive: (path: string) => boolean;
  compact: boolean;
  itemPad: string;
  onNavigate: () => void;
}

const storageKey = (id: string) => `sidebar-group-${id}`;

function readOpen(id: string): boolean {
  try {
    return localStorage.getItem(storageKey(id)) === '1';
  } catch {
    return false;
  }
}

function writeOpen(id: string, open: boolean): void {
  try {
    localStorage.setItem(storageKey(id), open ? '1' : '0');
  } catch {
    // хранилище недоступно: состояние живёт до перезагрузки
  }
}

function linkClass(active: boolean, itemPad: string, extra = ''): string {
  return `flex items-center gap-3 h-11 ${itemPad} rounded-lg transition-colors overflow-hidden ${extra} ${
    active ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
  }`;
}

export function SidebarGroup({
  id,
  icon: Icon,
  label,
  items,
  isActive,
  compact,
  itemPad,
  onNavigate,
}: SidebarGroupProps) {
  const groupActive = items.some((item) => isActive(item.path));
  const [open, setOpen] = useState(() => readOpen(id) || groupActive);

  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  if (items.length === 0) return null;

  if (compact) {
    return (
      <Link
        to={items[0].path}
        onClick={onNavigate}
        title={label}
        aria-label={label}
        className={linkClass(groupActive, itemPad)}
      >
        <Icon className="w-5 h-5 shrink-0" />
      </Link>
    );
  }

  const toggle = () => {
    const next = !open;
    setOpen(next);
    writeOpen(id, next);
  };
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`sidebar-group-${id}`}
        className={`flex w-full items-center gap-3 h-11 ${itemPad} rounded-lg transition-colors overflow-hidden ${
          groupActive && !open ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <Icon className="w-5 h-5 shrink-0" />
        <span className="truncate flex-1 text-left">{label}</span>
        <Chevron className="w-4 h-4 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div id={`sidebar-group-${id}`} className="mt-1 space-y-1 pl-4">
          {items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={linkClass(isActive(item.path), itemPad)}
              >
                <ItemIcon className="w-5 h-5 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
